import React, { useState, useRef, useEffect } from 'react';
import { Scenario, Category, TestResult, Project } from '../types';
import ErrorListModal from '../components/ErrorListModal';
import AddScenarioModal from '../components/AddScenarioModal';
import EditTestModal from '../components/EditTestModal';
import ScenarioEditorModal from '../components/ScenarioEditorModal';
import Button from '../components/Button';
import { handleStatusChange, handleBulkStatusChange } from '../services/statusService';
import { navigateToNextTest, navigateToPreviousTest } from '../services/navigationService';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import { useToast } from '../contexts/ToastContext';
import {
  TestManagerSidebar,
  TestManagerContent,
  TestManagerScenariosList,
  TestManagerShortcutsPanel,
  TestManagerStatsBanner,
  useTestManagerShortcuts,
  useTestManagerExport,
} from './TestManager/index';


interface TestManagerProps {
  categories: Category[];
  currentTest: TestResult;
  currentScenario: Scenario | null;
  selectedProject?: Project | null;
  onBack: () => void;
  onStatusChange: (status: string) => void;
  onNotesChange: (notes: string) => void;
  onScenarioSelect: (scenario: Scenario) => void;
  onCategoriesUpdate: (categories: Category[]) => void;
  onEditCSV?: () => void;
  onEditScenario?: (scenario: Scenario) => void;
  onAddScenario?: (scenario: Partial<Scenario>) => void;
  onTestUpdate?: (updatedTest: TestResult) => void;
  onDeleteTest?: (testId: string) => void;
  onDeleteScenario?: (testId: string, scenarioId: string) => void;
  onDeleteCategory?: (categoryName: string) => void;
}

const TestManager: React.FC<TestManagerProps> = ({
  categories,
  currentTest,
  currentScenario,
  selectedProject,
  onBack,
  onStatusChange,
  onNotesChange,
  onScenarioSelect,
  onCategoriesUpdate,
  onEditCSV,
  onEditScenario,
  onAddScenario,
  onTestUpdate,
  onDeleteTest,
  onDeleteScenario,
  onDeleteCategory
}) => {
  const { showToast } = useToast();
  const [currentCategory, setCurrentCategory] = useState<string>('');
  const [isErrorListOpen, setIsErrorListOpen] = useState(false);
  const [isAddScenarioOpen, setIsAddScenarioOpen] = useState(false);
  const [scenarioToDelete, setScenarioToDelete] = useState<Scenario | null>(null);
  const [isEditTestOpen, setIsEditTestOpen] = useState(false);
  const [isTestMenuOpen, setIsTestMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isTestDeleteModalOpen, setIsTestDeleteModalOpen] = useState(false);
  const [isStatsCollapsed, setIsStatsCollapsed] = useState(false);
  const [isShortcutsVisible, setIsShortcutsVisible] = useState(true);
  const [isAddingNotes, setIsAddingNotes] = useState(false);
  const notesTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [localNotes, setLocalNotes] = useState<string>('');
  const [isScenarioEditorOpen, setIsScenarioEditorOpen] = useState(false);
  const [scenarioToEdit, setScenarioToEdit] = useState<Scenario | null>(null);
  const [isCategoryDeleteModalOpen, setIsCategoryDeleteModalOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<string | null>(null);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);

  const API_BASE_URL = process.env.REACT_APP_API_HOST;

  const {
    isExportMenuOpen,
    setIsExportMenuOpen,
    exportMenuRef,
    exportFullReportCSVWrapper,
    exportFullReportExcelWrapper,
  } = useTestManagerExport(categories, currentTest);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsTestMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    // Sprawdź czy mamy zapisany ostatni test w localStorage
    const lastTestId = localStorage.getItem('lastTestId');
    if (lastTestId && !currentTest) {
      // Jeśli mamy zapisany test, ale nie jest on aktualnie załadowany, spróbuj go załadować
      fetch(`${API_BASE_URL}/api/tests/test_results/${lastTestId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })
        .then(response => {
          if (response.ok) {
            return response.json();
          }
          throw new Error('Nie udało się pobrać ostatniego testu');
        })
        .then(test => {
          if (onTestUpdate) {
            onTestUpdate(test);
          }
        })
        .catch(() => {
          localStorage.removeItem('lastTestId');
        });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- API_BASE_URL is env constant, onTestUpdate from props
  }, [currentTest, onTestUpdate]);

  const handleStatusChangeWrapper = (status: string) => {
    if (!currentScenario) return;
    
    handleStatusChange(
      status,
      currentScenario,
      currentCategory,
      categories,
      onScenarioSelect,
      onStatusChange,
      onCategoriesUpdate,
      showToast
    );
    showToast(`Status zmieniony na ${status}`, 'success');
  };

  const handleBulkStatusChangeWrapper = async (status: string) => {
    if (!currentTest) return;

    try {
      const updatedTest = await handleBulkStatusChange(
        status,
        currentCategory,
        categories,
        onCategoriesUpdate,
        currentTest
      );

      // Zapisz zmiany na serwerze
      const response = await fetch(`${API_BASE_URL}/api/tests/test_results/${currentTest.metadata.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(updatedTest),
      });

      if (!response.ok) {
        throw new Error(`Błąd HTTP: ${response.status}`);
      }

      // Aktualizuj stan testu
      if (onTestUpdate) {
        onTestUpdate(updatedTest);
      }

      showToast(`Status wszystkich testów w kategorii "${currentCategory}" został zaktualizowany`, 'success');
    } catch {
      showToast('Nie udało się zapisać wyników testów. Spróbuj ponownie.', 'error');
    }
  };

  const navigateToNextTestWrapper = () => {
    navigateToNextTest(
      currentCategory, 
      currentScenario, 
      categories, 
      onScenarioSelect,
      currentTest.metadata.selectedCategories
    );
  };

  const navigateToPreviousTestWrapper = () => {
    navigateToPreviousTest(
      currentCategory, 
      currentScenario, 
      categories, 
      onScenarioSelect,
      currentTest.metadata.selectedCategories
    );
  };

  const duplicateScenario = (scenario: Scenario) => {
    if (!onAddScenario) return;
    
    const { Status, Notes, ...basicScenarioData } = scenario;
    
    const duplicatedScenario: Partial<Scenario> = {
      ...basicScenarioData,
      'ID testu': scenario['ID testu'], // Zachowaj oryginalne ID dla poprawnego generowania nowego
      'Nazwa testu': `${basicScenarioData['Nazwa testu']} (kopia)`,
    };
    
    // Użyj onAddScenario, które zajmie się generowaniem ID i zapisaniem w CSV
    onAddScenario(duplicatedScenario);
    
    // Pokaż komunikat o sukcesie
    showToast('Scenariusz został zduplikowany', 'success');
  };

  // Funkcja do zamykania (czyszczenia) bieżącego scenariusza
  const clearCurrentScenario = () => {
    // Tylko zamknij scenariusz, nie resetuj kategorii
    onScenarioSelect(null as any);
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setScenarioToDelete(null);
  };

  const initiateDeleteScenario = (scenario: Scenario) => {
    setScenarioToDelete(scenario);
    setIsDeleteModalOpen(true);
  };

  // Aktualizuj lokalny stan notatek przy zmianie scenariusza
  const currentScenarioId = currentScenario?.['ID testu'];
  useEffect(() => {
    if (currentScenario) {
      setLocalNotes(currentScenario.Notes || '');
    }
  }, [currentScenario, currentScenarioId]);

  // Funkcja do zapisywania notatek do API
  const saveNotesToAPI = (notes: string) => {
    if (!currentScenario || !currentTest) return;

    // Update the scenario with new notes
    const updatedScenario = {
      ...currentScenario,
      Notes: notes
    };
    
    // Update the test results
    const updatedTest = {
      ...currentTest,
      scenarioResults: {
        ...currentTest.scenarioResults,
        [currentScenario['ID testu']]: {
          ...currentTest.scenarioResults[currentScenario['ID testu']],
          Notes: notes
        }
      }
    };

    // Call the handlers
    onScenarioSelect(updatedScenario);
    onNotesChange(notes);
    
    // Update the test on the server
    fetch(`${API_BASE_URL}/api/tests/test_results/${currentTest.metadata.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify(updatedTest),
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Failed to save notes');
      }
      if (onTestUpdate) {
        onTestUpdate(updatedTest);
      }
      showToast('Notatki zostały zapisane', 'success');
    })
    .catch(() => {
      showToast('Nie udało się zapisać notatek. Spróbuj ponownie.', 'error');
    });
  };

  // Lokalna aktualizacja notatek podczas pisania (bez zapisywania do API)
  const handleNotesChangeLocal = (notes: string) => {
    setLocalNotes(notes);
    
    // Aktualizuj lokalnie scenariusz (bez zapisu do API)
    if (currentScenario) {
      const updatedScenario = {
        ...currentScenario,
        Notes: notes
      };
      onScenarioSelect(updatedScenario);
    }
  };

  // Zapisz notatki po zakończeniu edycji (onBlur lub Enter)
  const handleNotesSave = () => {
    saveNotesToAPI(localNotes);
  };

  // Obsługa naciśnięcia klawisza
  const handleNotesKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter (bez Shift) - zapisz i zakończ edycję
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleNotesSave();
      notesTextareaRef.current?.blur();
      setIsAddingNotes(false);
    }
    // Escape - anuluj i przywróć oryginalne notatki
    if (e.key === 'Escape') {
      setLocalNotes(currentScenario?.Notes || '');
      notesTextareaRef.current?.blur();
      setIsAddingNotes(false);
    }
  };

  const handleEditTest = (data: {
    systemName: string;
    testerName: string;
    environment: string;
    version?: string;
    platforms: ('iOS' | 'Android' | 'Web')[];
  }) => {
    if (onTestUpdate && currentTest) {
      const updatedTest: TestResult = {
        ...currentTest,
        metadata: {
          ...currentTest.metadata,
          systemName: data.systemName,
          testerName: data.testerName,
          environment: data.environment,
          version: data.version,
          platforms: data.platforms
        }
      };

      // Zapisz zmiany na serwerze
      fetch(`${API_BASE_URL}/api/tests/test_results/${currentTest.metadata.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(updatedTest),
      })
      .then(response => {
        if (!response.ok) {
          throw new Error(`Błąd podczas zapisywania testu: ${response.status}`);
        }
        return response.json();
      })
      .then(() => {
        // Aktualizuj stan tylko po pomyślnym zapisie
        onTestUpdate(updatedTest);
        setIsEditTestOpen(false);
        showToast('Test został pomyślnie zaktualizowany', 'success');
      })
      .catch(() => {
        showToast('Nie udało się zapisać zmian. Spróbuj ponownie.', 'error');
      });
    }
  };

  const handleDeleteScenario = async (scenario: Scenario) => {
    if (onDeleteScenario && currentTest) {
      await onDeleteScenario(currentTest.metadata.id, scenario['ID testu']);
    }
  };

  const handleScenarioDeleteClick = (scenario: Scenario) => {
    initiateDeleteScenario(scenario);
  };

  const handleScenarioDeleteConfirm = async () => {
    if (scenarioToDelete && currentTest) {
      await handleDeleteScenario(scenarioToDelete);
      setIsDeleteModalOpen(false);
      setScenarioToDelete(null);
    }
  };

  const handleTestDeleteConfirm = async () => {
    if (currentTest && onDeleteTest) {
      try {
        // Usuń testId z pliku CSV w projekcie
        if (currentTest.metadata.projectId && currentTest.metadata.csvFileId) {
          const updateProjectResponse = await fetch(`${API_BASE_URL}/api/projects/update`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({
              projectId: currentTest.metadata.projectId,
              csvFileId: currentTest.metadata.csvFileId,
              testId: currentTest.metadata.id,
              action: 'remove'
            }),
          });

          if (!updateProjectResponse.ok) {
            // project list update failed
          }
        }

        // Usuń test
        await onDeleteTest(currentTest.metadata.id);
        setIsTestDeleteModalOpen(false);
        showToast('Test został pomyślnie usunięty', 'success');
      } catch {
        showToast('Wystąpił błąd podczas usuwania testu', 'error');
      }
    }
  };

  const handleEditScenarioClick = (scenario: Scenario) => {
    setScenarioToEdit(scenario);
    setIsScenarioEditorOpen(true);
  };

  const handleSaveScenario = async (updatedScenario: Scenario) => {
    if (onEditScenario) {
      onEditScenario(updatedScenario);
      setIsScenarioEditorOpen(false);
      setScenarioToEdit(null);
      showToast('Scenariusz został pomyślnie zaktualizowany', 'success');
    }
  };

  const handleCategoryDeleteClick = (categoryName: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Zapobiegaj wyborowi kategorii przy kliknięciu przycisku
    setCategoryToDelete(categoryName);
    setIsCategoryDeleteModalOpen(true);
  };

  const handleCategoryDeleteConfirm = async () => {
    if (categoryToDelete && onDeleteCategory) {
      await onDeleteCategory(categoryToDelete);
      setIsCategoryDeleteModalOpen(false);
      setCategoryToDelete(null);
      // Wyczyść aktualną kategorię, jeśli została usunięta
      if (currentCategory === categoryToDelete) {
        setCurrentCategory('');
        clearCurrentScenario();
      }
    }
  };

  useTestManagerShortcuts({
    currentScenario,
    categories,
    currentCategory,
    isAddingNotes,
    onStatusChange: handleStatusChangeWrapper,
    onNavigateNext: navigateToNextTestWrapper,
    onNavigatePrevious: navigateToPreviousTestWrapper,
    onClearScenario: clearCurrentScenario,
    onDuplicateScenario: duplicateScenario,
    onEditScenario: handleEditScenarioClick,
    setShortcutsVisible: setIsShortcutsVisible,
    setAddingNotes: setIsAddingNotes,
    notesTextareaRef,
    showToast,
  });

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-12 pt-24">
      <TestManagerShortcutsPanel
        isVisible={isShortcutsVisible}
        onClose={() => setIsShortcutsVisible(false)}
        onShow={() => setIsShortcutsVisible(true)}
      />

      <div className="w-full max-w-[2000px] mx-auto bg-white/60 dark:bg-slate-800/90 backdrop-blur-sm rounded-2xl shadow-lg p-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            <button
              onClick={onBack}
              className="mr-4 p-2 rounded-lg bg-white dark:bg-slate-700 shadow-lg hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors"
            >
              <svg
                className="w-6 h-6 text-gray-600 dark:text-gray-200"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
            </button>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white">Test Manager</h1>
          </div>
          <div className="flex items-center gap-4">
            <Button
              onClick={() => setIsErrorListOpen(true)}
              color="red"
              icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              }
              text="Lista błędów"
              badge={categories.reduce(
                  (acc, cat) => acc + cat.scenarios.filter(s => s.Status === 'Failed').length,
                  0
              )}
            />
            <Button
              onClick={() => setIsAddScenarioOpen(true)}
              color="blue"
              icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              }
              text="Dodaj scenariusz"
            />
            <div className="relative" ref={exportMenuRef}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsExportMenuOpen(!isExportMenuOpen);
                }}
                className="w-48 h-12 bg-green-500 hover:bg-green-600 text-white rounded-xl transition-all duration-200 shadow-md hover:shadow-xl flex items-center justify-center gap-2 relative"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>Generuj raport</span>
                <svg 
                  className={`w-4 h-4 transition-transform duration-200 ${isExportMenuOpen ? 'rotate-180' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {isExportMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-slate-700 rounded-lg shadow-xl border border-gray-200 dark:border-slate-600 z-50 overflow-hidden">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      exportFullReportCSVWrapper();
                    }}
                    className="w-full text-left px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-green-50 dark:hover:bg-slate-600 flex items-center gap-3 transition-colors"
                  >
                    <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <div className="flex flex-col">
                      <span className="font-medium">Eksportuj do CSV</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">Format tekstowy</span>
                    </div>
                  </button>
                  <div className="border-t border-gray-200 dark:border-slate-600"></div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      exportFullReportExcelWrapper();
                    }}
                    className="w-full text-left px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-green-50 dark:hover:bg-slate-600 flex items-center gap-3 transition-colors"
                  >
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <div className="flex flex-col">
                      <span className="font-medium">Eksportuj do Excel</span>
                      <span className="text-xs text-gray-500">Format .xlsx</span>
                    </div>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <TestManagerStatsBanner
          categories={categories}
          currentTest={currentTest}
          isStatsCollapsed={isStatsCollapsed}
          onStatsCollapsedChange={setIsStatsCollapsed}
          isTestMenuOpen={isTestMenuOpen}
          onTestMenuOpenChange={setIsTestMenuOpen}
          menuRef={menuRef}
          onEditCSV={onEditCSV}
          onEditTest={() => setIsEditTestOpen(true)}
          onDeleteTest={() => setIsTestDeleteModalOpen(true)}
        />

            <div className="mb-4">
          <button
            type="button"
            onClick={() => setIsSidebarVisible((v) => !v)}
            className={`flex items-center gap-2 px-3 py-2 rounded-xl transition-colors ${
              isSidebarVisible ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300' : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600'
            }`}
            title={isSidebarVisible ? 'Ukryj panel kategorii' : 'Pokaż panel kategorii'}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2M7 7h10" />
            </svg>
            <span className="hidden sm:inline">Kategorie</span>
            <svg
              className={`w-4 h-4 transition-transform ${isSidebarVisible ? '' : 'rotate-180'}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        </div>

        <div className="grid grid-cols-12 gap-8">
          {isSidebarVisible && (
            <TestManagerSidebar
              categories={categories}
              currentCategory={currentCategory}
              currentTest={currentTest}
              onCategorySelect={setCurrentCategory}
              onClearScenario={clearCurrentScenario}
              onDeleteCategory={onDeleteCategory}
              onCategoryDeleteClick={handleCategoryDeleteClick}
            />
          )}
          <div className={isSidebarVisible ? 'col-span-12 md:col-span-9 grid grid-cols-1 lg:grid-cols-2 gap-8' : 'col-span-12 grid grid-cols-1 lg:grid-cols-2 gap-8'}>
            <TestManagerScenariosList
              currentCategory={currentCategory}
              categories={categories}
              currentScenario={currentScenario}
              currentTest={currentTest}
              onScenarioSelect={onScenarioSelect}
              onBulkStatusPassed={() => handleBulkStatusChangeWrapper('Passed')}
              onBulkStatusNotTested={() => handleBulkStatusChangeWrapper('Not Tested')}
            />
            {currentScenario ? (
              <TestManagerContent
                currentScenario={currentScenario}
                currentCategory={currentCategory}
                categories={categories}
                localNotes={localNotes}
                isAddingNotes={isAddingNotes}
                notesTextareaRef={notesTextareaRef}
                onStatusChange={handleStatusChangeWrapper}
                onEditScenario={handleEditScenarioClick}
                onDuplicateScenario={duplicateScenario}
                onDeleteScenario={handleScenarioDeleteClick}
                onNavigatePrevious={navigateToPreviousTestWrapper}
                onNavigateNext={navigateToNextTestWrapper}
                onClearScenario={clearCurrentScenario}
                onNotesChangeLocal={handleNotesChangeLocal}
                onNotesSave={handleNotesSave}
                onNotesKeyDown={handleNotesKeyDown}
                onNotesFocus={() => setIsAddingNotes(true)}
              />
            ) : (
              <div className="bg-white dark:bg-slate-700/80 rounded-2xl shadow-xl p-6">
                <div className="text-center py-12">
                  <svg className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <p className="text-gray-500 dark:text-gray-400 text-lg">Wybierz scenariusz, aby zobaczyć szczegóły</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal z listą błędów */}
      <ErrorListModal 
        isOpen={isErrorListOpen}
        onClose={() => setIsErrorListOpen(false)}
        categories={categories}
        onScenarioSelect={onScenarioSelect}
      />

      {/* Modal do dodawania scenariusza */}
      <AddScenarioModal
        isOpen={isAddScenarioOpen}
        onClose={() => setIsAddScenarioOpen(false)}
        onSubmit={(scenario: Partial<Scenario>) => {
          onAddScenario?.(scenario);
          setIsAddScenarioOpen(false);
        }}
        categories={categories.map(cat => cat.name)}
        allScenarios={categories.flatMap(cat => cat.scenarios)}
      />
      
      {/* Modal potwierdzenia usunięcia scenariusza */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleScenarioDeleteConfirm}
        title="Usuń scenariusz"
        message={`Czy na pewno chcesz usunąć scenariusz "${scenarioToDelete?.['Nazwa testu']}"? Tej operacji nie można cofnąć.`}
      />

      {/* Modal edycji testu */}
      <EditTestModal
        isOpen={isEditTestOpen}
        onClose={() => setIsEditTestOpen(false)}
        onSubmit={handleEditTest}
        testData={currentTest}
      />

      {/* Modal potwierdzenia usunięcia testu */}
      <DeleteConfirmationModal
        isOpen={isTestDeleteModalOpen}
        onClose={() => setIsTestDeleteModalOpen(false)}
        onConfirm={handleTestDeleteConfirm}
        title="Usuń test"
        message="Czy na pewno chcesz usunąć ten test? Tej operacji nie można cofnąć."
      />

      {/* Modal edycji scenariusza */}
      <ScenarioEditorModal
        isOpen={isScenarioEditorOpen}
        onClose={() => setIsScenarioEditorOpen(false)}
        onSave={handleSaveScenario}
        scenario={scenarioToEdit}
      />

      {/* Modal potwierdzenia usunięcia kategorii */}
      <DeleteConfirmationModal
        isOpen={isCategoryDeleteModalOpen}
        onClose={() => {
          setIsCategoryDeleteModalOpen(false);
          setCategoryToDelete(null);
        }}
        onConfirm={handleCategoryDeleteConfirm}
        title="Usuń kategorię"
        message={`Czy na pewno chcesz usunąć całą kategorię "${categoryToDelete}"? Wszystkie scenariusze w tej kategorii zostaną oznaczone jako usunięte (isDeleted: true) w CSV i usunięte z bieżącego testu. Tej operacji nie można cofnąć.`}
      />
    </div>
  );
};

export default TestManager; 